import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle, Share2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacebookCallbackPage({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [pages, setPages] = useState([]);
  const [pendingData, setPendingData] = useState(null);
  const [connectingPage, setConnectingPage] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      setStatus("error");
      setErrorMsg(errorDescription || error || "Autorisation refusée");
      return;
    }

    if (!code) {
      setStatus("error");
      setErrorMsg("Aucun code d'autorisation reçu");
      return;
    }

    exchangedRef.current = true;
    exchangeCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCode = async (code) => {
    const redirectUri = `${window.location.origin}/auth/facebook/callback`;

    try {
      const response = await axios.post(`${API}/facebook/oauth/exchange`, {
        code,
        redirect_uri: redirectUri,
      });

      const { token, user, pages: fbPages } = response.data;

      if (fbPages && fbPages.length > 0) {
        setPendingData({ token, user });
        setPages(fbPages);
        setStatus("pages");
      } else {
        // No pages, just login
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        if (onLogin) onLogin(user);
        toast.success("Connexion Facebook réussie!");
        setStatus("success");
        setTimeout(() => navigate("/"), 1000);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'échange du code";
      if (msg.includes("has been used")) {
        // Code already used — redirect to home or login
        const existingToken = localStorage.getItem("token");
        if (existingToken) {
          navigate("/");
        } else {
          navigate("/login");
        }
        return;
      }
      setStatus("error");
      setErrorMsg(msg);
    }
  };

  const handlePageSelect = async (page) => {
    if (!pendingData) return;
    setConnectingPage(page.id);

    try {
      await axios.post(
        `${API}/facebook/pages/connect`,
        { page_id: page.id, page_name: page.name },
        { headers: { Authorization: `Bearer ${pendingData.token}` } }
      );
      toast.success(`Page "${page.name}" connectée!`);
    } catch (error) {
      toast.error("Erreur lors de la connexion de la page");
    }

    localStorage.setItem("token", pendingData.token);
    localStorage.setItem("user", JSON.stringify(pendingData.user));
    if (onLogin) onLogin(pendingData.user);
    setConnectingPage(null);
    setStatus("success");
    setTimeout(() => navigate("/"), 1000);
  };

  const skipPageSelection = () => {
    if (pendingData) {
      localStorage.setItem("token", pendingData.token);
      localStorage.setItem("user", JSON.stringify(pendingData.user));
      if (onLogin) onLogin(pendingData.user);
    }
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Share2 className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">SocialHub</h1>
        </div>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            {status === "loading" && (
              <div className="text-center py-8 space-y-4" data-testid="fb-callback-loading">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
                <p className="text-lg font-medium">Connexion en cours...</p>
                <p className="text-sm text-muted-foreground">Récupération de vos informations Facebook</p>
              </div>
            )}

            {status === "pages" && (
              <div className="space-y-4" data-testid="fb-callback-pages">
                <div className="text-center">
                  <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <h2 className="text-lg font-semibold">Connexion réussie!</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sélectionnez la page sur laquelle publier
                  </p>
                </div>

                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {pages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => handlePageSelect(page)}
                      disabled={connectingPage !== null}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left"
                      data-testid={`fb-page-${page.id}`}
                    >
                      {page.picture_url ? (
                        <img src={page.picture_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                          {page.name?.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{page.name}</p>
                        {page.category && (
                          <p className="text-sm text-muted-foreground">{page.category}</p>
                        )}
                      </div>
                      {connectingPage === page.id && (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>

                <Button
                  variant="ghost"
                  onClick={skipPageSelection}
                  className="w-full"
                  data-testid="fb-skip-pages"
                >
                  Passer cette étape
                </Button>
              </div>
            )}

            {status === "success" && (
              <div className="text-center py-8 space-y-4" data-testid="fb-callback-success">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                <p className="text-lg font-medium">Tout est configuré!</p>
                <p className="text-sm text-muted-foreground">Redirection en cours...</p>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-8 space-y-4" data-testid="fb-callback-error">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <p className="text-lg font-medium">Erreur de connexion</p>
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button variant="outline" onClick={() => navigate("/login")}>
                    Retour au login
                  </Button>
                  <Button onClick={() => navigate("/settings")}>
                    Paramètres
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
